import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Smartphone, Megaphone, Zap, Download, Upload, Loader2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import axios from './wabotAxios';
import { toast } from 'sonner';
import WaBotDevices from './WaBotDevices';
import WaBotMarketing from './WaBotMarketing';
import WaBotTriggers from './WaBotTriggers';
import { WABOT_API, WABOT_BACKEND_ORIGIN, isProductionHost } from './wabotApi';

const API = WABOT_API;

const TABS = [
  { id: 'devices',   label: 'המכשירים שלי',         icon: Smartphone, comp: WaBotDevices   },
  { id: 'marketing', label: 'דיוור הודעות וואצאפ', icon: Megaphone,  comp: WaBotMarketing },
  { id: 'triggers',  label: 'תגובה על טריגר',       icon: Zap,        comp: WaBotTriggers  },
];

const WaBotApp = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get('tab') || 'devices';
  const fileRef = useRef();
  const [working, setWorking] = useState(false);

  const setActive = (id) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    }, { replace: false });
  };
  const Active = (TABS.find(t => t.id === active) || TABS[0]).comp;

  // Export current trigger config to a downloadable JSON file.
  const onExport = async () => {
    setWorking(true);
    try {
      const r = await axios.get(`${API}/wabot/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bari-wabot-config-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`יוצא: ${r.data.counts?.triggers || 0} טריגרים, ${r.data.counts?.packs || 0} קבוצות`);
    } catch (e) {
      toast.error('שגיאה בייצוא');
    }
    setWorking(false);
  };

  // Import a JSON file produced by /wabot/export.
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setWorking(true);
    try {
      const text = await f.text();
      const data = JSON.parse(text);
      if (!data.triggers && !data.trigger_packs) {
        toast.error('הקובץ לא מכיל טריגרים או קבוצות'); setWorking(false); return;
      }
      const r = await axios.post(`${API}/wabot/import`, { data, mode: 'merge' });
      toast.success(
        `יובא: ${r.data.triggers.added} טריגרים חדשים, ${r.data.triggers.updated} עודכנו · ` +
        `${r.data.packs.added} קבוצות חדשות, ${r.data.packs.updated} עודכנו`
      );
      // Force a hard refresh so all open tabs re-fetch fresh data
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'שגיאה בייבוא - וודא שהקובץ תקין');
    }
    e.target.value = '';
    setWorking(false);
  };

  // CRITICAL: hooks above (must be unconditional). On production hosts we
  // transparently route every wabot API call to the always-running preview
  // backend via WABOT_API - no need to redirect users away anymore.
  const onProd = isProductionHost();

  // Live health-check of the wabot backend connection. Tolerant to transient
  // blips: needs 3 consecutive failures before turning red (a single 404
  // during hot-reload should never alarm the user). Polls every 8 seconds
  // when healthy, every 3 seconds when degraded.
  const [health, setHealth] = useState({ ok: null, count: 0, ms: 0, err: '' });
  const failStreakRef = useRef(0);
  useEffect(() => {
    let alive = true;
    let timer;
    const ping = async () => {
      const t0 = performance.now();
      try {
        // Use the axios wrapper that already retries internally. Mark with
        // __no_retry so this health check itself is single-shot - we don't
        // want to compound the wrapper's own backoff with our streak logic.
        const r = await axios.get(`${WABOT_API}/wabot/sessions`, { timeout: 8000, __no_retry: true });
        if (!alive) return;
        failStreakRef.current = 0;
        const count = (r.data?.sessions || []).length;
        setHealth({ ok: true, count, ms: Math.round(performance.now() - t0), err: '' });
      } catch (e) {
        if (!alive) return;
        failStreakRef.current += 1;
        // Only show red after 3 straight fails (covers ~24s of degraded state)
        if (failStreakRef.current >= 3) {
          setHealth({ ok: false, count: 0, ms: 0, err: e?.message || 'network error' });
        }
      }
      const nextDelay = failStreakRef.current ? 3000 : 8000;
      timer = setTimeout(ping, nextDelay);
    };
    ping();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, []);

  const hardReload = () => {
    try {
      // 1) clear caches (covers older service-worker cache bust)
      if ('caches' in window) {
        caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k))));
      }
      // 2) unregister any service workers (none expected on /bari, but safe)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister()));
      }
    } catch (_e) {}
    // 3) cache-busted reload
    const u = new URL(window.location.href);
    u.searchParams.set('_cb', Date.now().toString());
    window.location.replace(u.toString());
  };

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl" data-testid="wabot-app">
      {/* Persistent live status bar - proves to the user the bot is talking
          to the right backend and shows session count in real time. */}
      <div
        className={`px-4 py-2 text-xs font-bold flex items-center justify-between gap-3 border-b-2 ${
          health.ok === null
            ? 'bg-stone-100 border-stone-300 text-stone-700'
            : health.ok
              ? 'bg-green-50 border-green-300 text-green-800'
              : 'bg-red-50 border-red-300 text-red-800'
        }`}
        data-testid="wabot-status-bar"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {health.ok === null && <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />}
          {health.ok === true && <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
          {health.ok === false && <XCircle className="w-4 h-4 flex-shrink-0" />}
          <span className="truncate">
            {health.ok === null && 'בודק חיבור לשירות הבוט...'}
            {health.ok === true && (
              <>
                ✓ מחובר · <b>{health.count}</b> מכשירים פעילים · <span className="text-green-600">{health.ms}ms</span>
              </>
            )}
            {health.ok === false && (
              <>שגיאת חיבור: {health.err} — לחץ "טען מחדש"</>
            )}
          </span>
          <span className="text-stone-400 hidden sm:inline" dir="ltr">
            {WABOT_BACKEND_ORIGIN || '(same-origin)'}
          </span>
        </div>
        <button
          type="button"
          onClick={hardReload}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white hover:bg-stone-50 border border-stone-300 text-stone-700 text-[11px] font-bold flex-shrink-0"
          data-testid="wabot-hard-reload"
          title="ניקוי מטמון וטעינה מחדש"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          טען מחדש
        </button>
      </div>
      {onProd && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-center text-[11px] text-amber-800">
          הבוט מנוהל מהשרת הפנימי הקבוע — הנתונים שמורים אוטומטית במסד הנתונים.
        </div>
      )}
      {/* Tabs Bar */}
      <div className="bg-white border-b border-stone-200 px-6 py-3 flex items-center gap-2 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-2 ml-4">
          <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-black text-[#2C3E2D]">בוט וואצאפ</p>
            <p className="text-[10px] text-stone-400">מערכת פנימית</p>
          </div>
        </div>
        <div className="h-8 w-px bg-stone-200 mx-2" />
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = active === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActive(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                isActive
                  ? 'bg-[#D97706] text-white shadow-md'
                  : 'text-stone-600 hover:bg-stone-100'
              }`}
              data-testid={`wabot-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
        {/* Export / Import buttons - critical for moving config preview→prod */}
        <div className="flex-1" />
        <button
          onClick={onExport}
          disabled={working}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 disabled:opacity-50"
          data-testid="wabot-export-btn"
          title="הורדת כל הטריגרים והקבוצות לקובץ JSON"
        >
          {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          ייצוא
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={working}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 disabled:opacity-50"
          data-testid="wabot-import-btn"
          title="העלאת קובץ JSON וטעינת כל הטריגרים והקבוצות"
        >
          <Upload className="w-4 h-4" />
          ייבוא
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          onChange={onImportFile}
          className="hidden"
          data-testid="wabot-import-file"
        />
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto">
        <Active />
      </div>
    </div>
  );
};

export default WaBotApp;
